import { ApplicationConfig, ErrorHandler, inject, isDevMode, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { provideRouter, TitleStrategy, withComponentInputBinding, withRouterConfig } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';

import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { routes } from './app.routes';

import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import { AuthService } from './services/auth.service';

import {
  LocaleModule,
  ModuleRegistry,
  ClientSideRowModelModule,
  ColumnApiModule,
  CustomFilterModule,
  DateFilterModule,
  TextFilterModule,
  NumberFilterModule,
  NumberEditorModule,
  TextEditorModule,
  SelectEditorModule,
  CustomEditorModule,
  ValidationModule,
  CellStyleModule,
  RenderApiModule,
  RowApiModule,
  RowSelectionModule,
  RowStyleModule,
  CsvExportModule,
  EventApiModule,
  CellSpanModule,
  HighlightChangesModule,
  UndoRedoEditModule,
} from 'ag-grid-community';
import {
  SetFilterModule,
  ClipboardModule,
  CellSelectionModule,
  ServerSideRowModelModule,
  ServerSideRowModelApiModule,
  LicenseManager,
} from 'ag-grid-enterprise';
import { environment } from '../../environment/environment';

LicenseManager.setLicenseKey(environment.agGrid.licenseKey);

ModuleRegistry.registerModules([
  CellSelectionModule,
  CellSpanModule,
  CellStyleModule,
  ClientSideRowModelModule,
  ClipboardModule,
  ColumnApiModule,
  CsvExportModule,
  CustomEditorModule,
  CustomFilterModule,
  DateFilterModule,
  EventApiModule,
  HighlightChangesModule,
  LocaleModule,
  NumberEditorModule,
  NumberFilterModule,
  RenderApiModule,
  RowApiModule,
  RowSelectionModule,
  RowStyleModule,
  SelectEditorModule,
  ServerSideRowModelApiModule,
  ServerSideRowModelModule,
  SetFilterModule,
  TextEditorModule,
  TextFilterModule,
  UndoRedoEditModule,
  ValidationModule,
]);

import {
  MsalBroadcastService,
  MsalGuard,
  MsalInterceptor,
  MsalService,
  MSAL_BROADCAST_CONFIG,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
} from '@azure/msal-angular';

import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import { provideTranslocoMessageformat } from '@jsverse/transloco-messageformat';
import { firstValueFrom } from 'rxjs';
import { TranslocoHttpLoader } from './transloco-loader';
import { ConfirmationService, MessageService } from 'primeng/api';
import { MSAL_GUARD_CONFIG_FACTORY, MSAL_INSTANCE_FACTORY, MSAL_INTERCEPTOR_CONFIG_FACTORY } from '../config/MSALConfig';
import { MyPreset } from '../theme-style';
import { DialogService } from 'primeng/dynamicdialog';
import frLocale from 'primelocale/fr.json';
import { apiBaseUrlInterceptor } from './interceptors/api-base-url.interceptor';
import { AuthRefreshInterceptor } from './interceptors/auth-refresh.interceptor';
import { DateInterceptor } from './interceptors/date.interceptor';
import { HttpErrorInterceptor } from './interceptors/http-error.interceptor';
import { LoadingInterceptor } from './interceptors/loading.interceptor';
import { MultilingualJsonInterceptor } from './interceptors/multilingual-json.interceptor';
import { API_BASE_URL } from './services/api/api-base-url.token';
import { AssetContextManager } from './services/asset-context-manager.service';
import { CesGlobalErrorHandler } from './services/chunk-load-error.handler';
import { BusinessRulesService } from './services/business-rules.service';
import { CustomFilterService } from './services/custom-filter.service';
import { FormRegistryService } from './services/form-registry.service';
import { StoreInterfaceService } from './services/store-interface.service';
import { ToastService } from './services/toast.service';
import { TranslocoTitleStrategy } from './services/transloco-title.strategy';
import { ALL_STORE_PROVIDERS } from './stores/store.providers';

export const appConfig: ApplicationConfig = {
  providers: [
    {
      provide: MSAL_INSTANCE,
      useFactory: MSAL_INSTANCE_FACTORY,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSAL_GUARD_CONFIG_FACTORY,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSAL_INTERCEPTOR_CONFIG_FACTORY,
    },
    {
      provide: MSAL_BROADCAST_CONFIG,
      useValue: { eventsToReplay: 1 },
    },
    MsalService,
    MsalBroadcastService,
    MsalGuard,
    provideAppInitializer(() => {
      inject(AuthService);
    }),
    ALL_STORE_PROVIDERS,
    MessageService,
    BusinessRulesService,
    StoreInterfaceService,
    DialogService,
    FormRegistryService,
    CustomFilterService,
    ConfirmationService,
    ToastService,
    { provide: TitleStrategy, useClass: TranslocoTitleStrategy },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      // AuthRefreshInterceptor sits adjacent to MsalInterceptor so it can
      // catch 401s the cached Bearer triggers (CA drift, audience change,
      // server-side revocation) and replay with a forced silent refresh.
      provide: HTTP_INTERCEPTORS,
      useClass: AuthRefreshInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoadingInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MultilingualJsonInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: DateInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true,
    },
    { provide: API_BASE_URL, useValue: environment.apiUrl },
    { provide: ErrorHandler, useClass: CesGlobalErrorHandler },
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideTransloco({
      config: {
        availableLangs: ['de', 'en', 'fr', 'it'],
        defaultLang: 'fr',
        fallbackLang: 'en',
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
    provideTranslocoMessageformat(),
    provideAppInitializer(() => {
      const translocoService = inject(TranslocoService);
      let lang = localStorage.getItem('lang');

      if (!lang) {
        const browserLang = navigator.language.split('-')[0] ?? 'fr';
        const availableLangs = translocoService.getAvailableLangs() as string[];
        lang = availableLangs.includes(browserLang) ? browserLang : 'fr';
        localStorage.setItem('lang', lang);
      }

      const activeLang = lang;
      translocoService.setActiveLang(activeLang);
      return firstValueFrom(translocoService.load(activeLang));
    }),
    provideAppInitializer(() => {
      const customFilterService = inject(CustomFilterService);
      customFilterService.ensureFiltersRegistered();
      return Promise.resolve();
    }),
    provideAppInitializer(() => {
      inject(AssetContextManager);
      return Promise.resolve();
    }),
    provideAnimationsAsync(),
    provideHttpClient(withInterceptors([apiBaseUrlInterceptor]), withInterceptorsFromDi()),
    provideRouter(
      routes,
      withRouterConfig({
        onSameUrlNavigation: 'reload',
        paramsInheritanceStrategy: 'always',
      }),
      withComponentInputBinding(),
    ),
    provideZoneChangeDetection({ eventCoalescing: true }),
    providePrimeNG({
      theme: {
        preset: MyPreset,
        options: {
          darkModeSelector: false,
          cssLayer: {
            name: 'components',
          },
        },
      },
      translation: frLocale.fr,
    }),
  ],
};
